import React, { useEffect, useRef, useState } from "react";
import { Box, Flex } from "theme-ui";
import { navigate } from "gatsby";
import MuxPlayer from "@mux/mux-player-react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { colors, typography } from "../../styles/theme";
import { Button, Icon } from "../atoms";

const TabDisplay = ({
  sectionHeading,
  sectionDescription,
  featuresList,
  regionCode,
  pageName,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const buttonContainerRefs = useRef(
    featuresList?.map(() => React.createRef())
  );
  const [isOverflowing, setIsOverflowing] = useState(
    featuresList?.map(() => false)
  );

  const handleTabChange = (featureIndex) => {
    setActiveTab(featureIndex);
  };

  const scroll = (featureIndex, direction) => {
    const ref = buttonContainerRefs?.current[featureIndex];
    if (ref?.current) {
      const scrollAmount = 100;
      ref.current.scrollBy({
        left: direction === "right" ? scrollAmount : -scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const updateOverflowingState = () => {
    buttonContainerRefs?.current?.forEach((ref, index) => {
      if (ref?.current) {
        const containerWidth = ref?.current?.offsetWidth;
        const contentWidth = ref?.current?.scrollWidth;

        setIsOverflowing((prev) => {
          const newOverflowingState = [...prev];
          newOverflowingState[index] = contentWidth > containerWidth;
          return newOverflowingState;
        });
      }
    });
  };

  useEffect(() => {
    updateOverflowingState();
    const resizeObserver = new ResizeObserver(updateOverflowingState);
    buttonContainerRefs?.current?.forEach((ref) => {
      if (ref?.current) {
        resizeObserver?.observe(ref?.current);
      }
    });

    return () => {
      resizeObserver?.disconnect();
    };
  }, [featuresList]);

  useEffect(() => {
    updateOverflowingState();
  }, [activeTab]);

  return (
    <Box sx={outerContainer}>
      <Box
        as="h2"
        dangerouslySetInnerHTML={{ __html: sectionHeading }}
        sx={{
          p: { m: 0 },
          ...typography.h2,
          textAlign: "center",
        }}
      />
      {sectionDescription && (
        <Box
          dangerouslySetInnerHTML={{ __html: sectionDescription }}
          sx={{
            p: { m: 0 },
            ...typography.textMd400,
            textAlign: "center",
            mt: "1.5rem",
          }}
        />
      )}
      <Flex
        sx={{
          flexDirection: "column",
        }}
      >
        <Flex id="tabs-container" sx={tabsContainerStyles}>
          {isOverflowing[activeTab] && (
            <Flex sx={{ ...leftIconContainer }}>
              <Icon
                name="ChevronLeft"
                color={colors.primaryMain}
                pointerCursor="cursor"
                size={24}
                onClick={() => scroll(activeTab, "left")}
                customStyles={iconsCommonStyles}
              />
            </Flex>
          )}
          <Flex
            ref={buttonContainerRefs?.current[activeTab]}
            sx={buttonContainerStyles}
          >
            {featuresList?.map((item, index) => (
              <Flex
                key={`${item?.title}-${index}`}
                onClick={() => handleTabChange(index)}
                sx={{
                  ...buttonStyles,
                  backgroundColor:
                    activeTab === index ? colors.primary100 : "transparent",
                  color:
                    activeTab === index
                      ? colors.primaryMain
                      : colors.neutral900,
                  flexShrink: 0,
                  "&:hover": {
                    ...typography.h6,
                    color: colors.primaryMain,
                  },
                }}
              >
                {activeTab === index && (
                  <Icon
                    size={20}
                    name={item?.iconName}
                    color={colors.primaryMain}
                  />
                )}
                {item?.title}
              </Flex>
            ))}
          </Flex>
          {isOverflowing[activeTab] && (
            <Flex sx={{ ...rightIconContainer }}>
              <Icon
                name="ChevronRight"
                size={32}
                pointerCursor="cursor"
                color={colors.primaryMain}
                onClick={() => scroll(activeTab, "right")}
                customStyles={iconsCommonStyles}
              />
            </Flex>
          )}
        </Flex>
        <Flex id="tabs-body" sx={tabsBodyStyles}>
          <Flex sx={textContainerStyles}>
            <Box
              as="h3"
              dangerouslySetInnerHTML={{
                __html: featuresList[activeTab]?.sections[0]?.heading,
              }}
              sx={{ p: { m: 0 }, ...typography.h3 }}
            />
            {featuresList[activeTab]?.sections[0]?.description && (
              <Box
                dangerouslySetInnerHTML={{
                  __html: featuresList[activeTab]?.sections[0]?.description,
                }}
                sx={{
                  p: { m: 0 },
                  ...typography.textMd400,
                  ...listStyles(
                    colors[
                      `${featuresList[activeTab]?.sections[0]?.bulletColor}700`
                    ]
                  ),
                }}
              />
            )}
            {(featuresList[activeTab]?.primaryCtaName ||
              featuresList[activeTab]?.secondaryCtaName) && (
              <Flex
                sx={{
                  mt: "3rem",
                  gap: "2rem",
                  flexDirection: ["column", "column", "row", "row"],
                }}
              >
                {featuresList[activeTab]?.primaryCtaName && (
                  <Button
                    size="large"
                    id={`${pageName.toLowerCase()}_tab_display_primary_${regionCode.toLowerCase()}_btn`}
                    onClick={() =>
                      window.open(
                        featuresList[activeTab]?.primaryCtaUrl,
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                    variant="filled"
                    customStyles={{ width: "max-content" }}
                  >
                    {featuresList[activeTab]?.primaryCtaName}
                  </Button>
                )}
                {featuresList[activeTab]?.secondaryCtaName && (
                  <Button
                    size="large"
                    id={`${pageName.toLowerCase()}_tab_display_secondary_${regionCode.toLowerCase()}_btn`}
                    onClick={() =>
                      window.open(
                        featuresList[activeTab]?.secondaryCtaUrl,
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                    variant="outlined"
                    customStyles={{ width: "max-content" }}
                  >
                    {featuresList[activeTab]?.secondaryCtaName}
                  </Button>
                )}
              </Flex>
            )}
          </Flex>
          <Flex
            sx={{
              maxWidth: ["100%", "100%", "100%", "36.313rem"],
              justifyContent: "center",
              alignItems: "center",
              flexShrink: 1,
            }}
          >
            {featuresList[activeTab]?.sections[0]?.image && (
              <GatsbyImage
                image={getImage(featuresList[activeTab]?.sections[0]?.image)}
                alt="feature"
                style={{ width: "100%" }}
              />
            )}
            {featuresList[activeTab]?.sections[0]?.video?.video
              ?.muxPlaybackId && (
              <MuxPlayer
                width="100%"
                height="100%"
                playbackId={
                  featuresList[activeTab]?.sections[0]?.video?.video
                    ?.muxPlaybackId
                }
                autoPlay={true}
                loop={true}
                muted={true}
                poster={`https://image.mux.com/${featuresList[activeTab]?.sections[0]?.video?.video?.muxPlaybackId}/thumbnail.webp?width=1800&height=900&fit_mode=crop&time=1`}
                preload="auto"
              />
            )}
            {featuresList[activeTab]?.sections[0]?.videoId && (
              <video
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                width="100%"
                height="100%"
              >
                <source
                  src={featuresList[activeTab]?.sections[0]?.videoId}
                  type="video/mp4"
                />
              </video>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

const listStyles = (bulletColor) => ({
  ...typography.textMd400,
  color: colors.neutral900,
  "& ul": {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  "& li": {
    display: "flex",
    marginBottom: "1rem",
    mt: "1rem",
    "&:first-child": {
      mt: "1.5rem",
    },
  },
  "& .circle": {
    backgroundColor: bulletColor,
    width: "1.125rem",
    height: "1.125rem",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginRight: "0.531rem",
    marginTop: "0.2rem",
    flexShrink: 0,
  },
  "& p": {
    mt: ["1.5rem", "1.5rem", "0.75rem", "1rem"],
  },
});

const {
  buttonStyles,
  buttonContainerStyles,
  leftIconContainer,
  rightIconContainer,
  iconsCommonStyles,
  textContainerStyles,
  tabsBodyStyles,
  tabsContainerStyles,
  outerContainer,
} = {
  buttonStyles: {
    ...typography.h7,
    py: "1rem",
    px: "1.5rem",
    borderRadius: "9999px",
    cursor: "pointer",
    justifyContent: "center",
    alignItems: "center",
    gap: "0.5rem",
    "&:hover": {
      color: colors.primaryMain,
    },
  },
  buttonContainerStyles: {
    maxWidth: "71.938rem",
    gap: "0.5rem",
    border: `1px solid ${colors.neutral200}`,
    borderRadius: "9999px",
    px: "3rem",
    py: "0.75rem",
    overflowX: "scroll",
    justifyContent: "flex-start",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
    position: "relative",
  },
  leftIconContainer: {
    width: "3.125rem",
    height: "4.75rem",
    position: "absolute",
    left: 0,
    zIndex: 1,
    justifyContent: "center",
    alignItems: "center",
    background:
      "linear-gradient(to right, #ffffff, #ffffff, #ffffff, transparent)",
  },
  rightIconContainer: {
    width: "3.125rem",
    height: "4.75rem",
    position: "absolute",
    right: 0,
    zIndex: 1,
    justifyContent: "center",
    alignItems: "center",
    background:
      "linear-gradient(to left, #ffffff, #ffffff, #ffffff, transparent)",
  },
  iconsCommonStyles: {
    width: "1.5rem",
    height: "4.5rem",
    display: "flex",
  },
  textContainerStyles: {
    maxWidth: ["100%", "100%", "100%", "30.563rem"],
    py: "2.688rem",
    pl: "3rem",
    boxSizing: "content-box",
    "@media (max-width: 899px)": {
      order: 1,
      px: "3rem",
      pb: "3rem",
      pt: 0,
    },
    "@media (max-width: 599px)": {
      order: 1,
      px: "0.75rem",
      pb: "0.75remrem",
      pt: 0,
    },
    flexDirection: "column",
    flexShrink: 10,
    "@media (min-width:900px)": {
      minWidth: "22.5rem",
    },
  },
  tabsBodyStyles: {
    border: `1px solid ${colors.neutral200}`,
    borderRadius: "1.5rem",
    gap: ["1.5rem", "1.5rem", "4rem", "2rem"],
    mt: ["1.5rem", "1.5rem", "2rem", "2rem"],
    flexDirection: ["column", "column", "column", "row"],
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    maxWidth: ["100%", "100%", "100%", "71.875rem"],
    margin: "auto",
  },
  tabsContainerStyles: {
    position: "relative",
    margin: "0 auto",
    maxWidth: "100%",
    width: "fit-content",
    mt: ["3rem", "3rem", "4rem", "3rem"],
  },
  outerContainer: {
    maxWidth: "71.875rem",
    px: ["1rem", "1rem", "2rem", "4rem"],
    boxSizing: "content-box",
    justifyContent: "center",
    position: "relative",
    left: "50%",
    transform: "translateX(-50%)",
   },
};

export default TabDisplay;
